import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import axiosInstance from "../../api/axiosConfig";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCompany } from "../../context/CompanyContext";
import { useSubscription } from "../../hooks/useSubscription";
import { useAuth } from "../../context/AuthContext";
import { ThemeContext } from "../../context/ThemeContext";
import darkLogo from "../../assets/logo/dark.png";
import lightLogo from "../../assets/logo/light.png";
import miniLogo from "../../assets/logo/NUMRA.png";
import {
  faHome,
  faCommentDots,
  faUsers,
  faFolder,
  faArchive,
  faQuestionCircle,
  faCog,
  faFileAlt,
  faPlusSquare,
  faChevronDown,
  faBook,
  faTruck,
  faBars,
  faTimes,
  faDollarSign,
  faQuoteRight,
  faCreditCard,
  faShoppingBag,
  faSyncAlt,
  faChartBar,
  faShoppingCart,
  faClipboard,
  faFileInvoice,
  faUndoAlt,
  faArrowTrendUp,
  faClipboardList,
  faComments,
  faChartPie,
  faBox,
  faTag,
  faList,
  faChartLine,
  faMoneyBillWave,
  faLayerGroup,
  faClock,
  faShield,
  faUser,
  faUserCheck,
  faLock,
  faCodeBranch,
  faThLarge,
  faBriefcase,
  faWarehouse,
  faReceipt,
  faCalculator,
  faBalanceScale,
  faChartArea,
  faFileContract,
  faMoneyCheck,
  faHandHoldingUsd,
  faCalendarAlt,
  faMagnifyingGlass,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import "./Sidebar.css";

// Map an FE route path prefix to the logical module name that
// SuperAdmin toggles on/off per organization. Kept in sync with
// PATH_TO_MODULE in NUMRA-BK/src/middleware/moduleGate.js — a path
// that isn't listed here is treated as "always visible" (the same
// fail-open the BE gate uses).
const PATH_MODULE_MAP = [
  ["/accounts", "accounting"], ["/accruals", "accounting"], ["/inter-company", "accounting"],
  ["/approvals", "accounting"], ["/settings/exchange-rates", "accounting"],
  ["/settings/withholding-taxes", "accounting"], ["/settings/cost-centers", "accounting"],
  ["/settings/posting-rules", "accounting"], ["/settings/jv-numbering", "accounting"],
  ["/settings/tax-codes", "accounting"], ["/settings/approval-rules", "accounting"],
  ["/setup/opening-balances", "accounting"], ["/sales", "sales"], ["/customers", "sales"],
  ["/collections", "sales"], ["/quotes", "sales"], ["/payment", "sales"], ["/payments", "sales"],
  ["/invoice", "sales"], ["/purchases", "purchases"], ["/vendors", "purchases"],
  ["/expense-bills", "purchases"], ["/inventory", "inventory"], ["/categories", "inventory"],
  ["/products", "inventory"], ["/services", "inventory"], ["/hr/employees", "hr"],
  ["/hr/operations", "hr"], ["/hr/advances", "hr"], ["/hr/settlements", "hr"],
  ["/expense-reimbursements", "hr"], ["/hr/payroll", "payroll"], ["/hr/payslip", "payroll"],
  ["/hr/payslips", "payroll"], ["/hr/payroll-reports", "payroll"], ["/settings/payroll-tax", "payroll"],
  ["/production", "production"], ["/assets", "assets"], ["/imports", "imports"], ["/reports", "reports"],
];

function moduleForPath(path) {
  if (!path) return null;
  for (const [prefix, module] of PATH_MODULE_MAP) {
    if (path === prefix || path.startsWith(prefix + "/")) return module;
  }
  return null;
}

const iconMap = {
  Home: faHome,
  MessageSquare: faCommentDots,
  Users: faUsers,
  Folder: faFolder,
  Archive: faArchive,
  HelpCircle: faQuestionCircle,
  Settings: faCog,
  FileText: faFileAlt,
  PlusSquare: faPlusSquare,
  Book: faBook,
  Truck: faTruck,
  Menu: faBars,
  X: faTimes,
  DollarSign: faDollarSign,
  Quote: faQuoteRight,
  CreditCard: faCreditCard,
  ShoppingBag: faShoppingBag,
  RefreshCw: faSyncAlt,
  BarChart2: faChartBar,
  ShoppingCart: faShoppingCart,
  Clipboard: faClipboard,
  Bill: faFileInvoice,
  RotateCcw: faUndoAlt,
  TrendingUp: faArrowTrendUp,
  RequestForm: faClipboardList,
  VendorQuote: faComments,
  PieChart: faChartPie,
  Package: faBox,
  Tag: faTag,
  List: faList,
  BarChart: faChartLine,
  Tax: faMoneyBillWave,
  Layers: faLayerGroup,
  Clock: faClock,
  Shield: faShield,
  User: faUser,
  UserCheck: faUserCheck,
  Lock: faLock,
  GitBranch: faCodeBranch,
  Grid: faThLarge,
  Briefcase: faBriefcase,
  Warehouse: faWarehouse,
  Receipt: faReceipt,
  Calculator: faCalculator,
  BalanceScale: faBalanceScale,
  ChartArea: faChartArea,
  FileContract: faFileContract,
  MoneyCheck: faMoneyCheck,
  HandHoldingUsd: faHandHoldingUsd,
  Calendar: faCalendarAlt,
};

// `group` drives the sentence-case section dividers rendered between rows.
const menuItems = [
  {
    group: "Overview",
    icon: "Home",
    label: "Dashboard",
    requiredFeature: "dashboard",
    minPlan: "pro",
    submenu: [
      { path: "/dashboard", icon: "ChartArea", label: "Main Overview", requiredFeature: "dashboard", minPlan: "pro" },
      { path: "/purchases/dashboard", icon: "ShoppingCart", label: "Purchases", requiredFeature: "dashboard", minPlan: "team" },
      { path: "/sales/dashboard", icon: "DollarSign", label: "Sales", requiredFeature: "dashboard", minPlan: "team" },

      { path: "/inventory/dashboard", icon: "Package", label: "Inventory", requiredFeature: "dashboard", minPlan: "team" },
      { path: "/finance/dashboard", icon: "Calculator", label: "Finance", requiredFeature: "dashboard", minPlan: "team" },
      // Hidden for demo (out of 60% scope):
      // { path: "/admin/status-dashboard", icon: "Shield", label: "Admin", requiredFeature: "collaboration", minPlan: "team" },
    ],
  },
  {
    group: "Overview",
    icon: "BarChart",
    label: "Reports",
    minPlan: "free",
    submenu: [
      { path: "/reports/profit-loss", icon: "TrendingUp", label: "Profit & Loss", requiredFeature: "profitLoss", minPlan: "pro", viewOnly: true },
      { path: "/reports/balance-sheet", icon: "BalanceScale", label: "Balance Sheet", requiredFeature: "balanceSheet", minPlan: "pro", viewOnly: true },
      { path: "/reports/cashflow", icon: "DollarSign", label: "Cash Flow", requiredFeature: "cashflow", minPlan: "team" },
      { path: "/reports/trial-balance", icon: "Clipboard", label: "Trial Balance", requiredFeature: "trialBalance", minPlan: "free" },
      // { path: "/reports/chart-of-accounts", icon: "Book", label: "Chart of Accounts", requiredFeature: "chartOfAccounts", minPlan: "pro", viewOnly: true },
            { path: "/reports/sales", icon: "BarChart2", label: "Sales Reports", requiredFeature: "analytics", minPlan: "team" },




      { path: "/purchases/reports", icon: "TrendingUp", label: "Purchase Reports", requiredFeature: "analytics", minPlan: "team" },
      { path: "/inventory/reports", icon: "BarChart2", label: "Inventory Reports", requiredFeature: "analytics", minPlan: "team" },
      // Hidden for demo (duplicate / out of 60% scope):
      // { path: "/purchases/analytics", icon: "PieChart", label: "Stock Analytics", requiredFeature: "analytics", minPlan: "team" },
      { path: "/reports/corporate-tax", icon: "Tax", label: "Corporate Tax", requiredFeature: "taxReports", minPlan: "pro", viewOnly: true },
      // { path: "/reports/vat", icon: "FileText", label: "VAT", requiredFeature: "vat", minPlan: "pro", viewOnly: true },
      { path: "/reports/tax", icon: "Tax", label: "General Tax Summary", requiredFeature: "taxReports", minPlan: "pro", viewOnly: true },
      // { path: "/reports/customers/aging", icon: "Users", label: "Customer Aging", requiredFeature: "customerReports", minPlan: "team" },
      // { path: "/reports/vendors/aging", icon: "Truck", label: "Vendor Aging", requiredFeature: "vendorReports", minPlan: "team" },
      // { path: "/reports/ap-reconciliation", icon: "Clipboard", label: "AP Reconciliation", minPlan: "free" },
      // { path: "/reports/postings", icon: "FileText", label: "Postings", minPlan: "free" },
      // Hidden for demo — Professional reports duplicate the standard ones above:
      // { path: "/reports/professional/trial-balance", icon: "FileContract", label: "Trial Balance (Pro)", minPlan: "free" },
      // { path: "/reports/professional/profit-loss", icon: "FileContract", label: "P&L (Pro)", minPlan: "free" },
      // { path: "/reports/professional/balance-sheet", icon: "FileContract", label: "Balance Sheet (Pro)", minPlan: "free" },
      // { path: "/reports/professional/cashflow", icon: "FileContract", label: "Cash Flow (Pro)", minPlan: "free" },
      // { path: "/reports/professional/customer-aging", icon: "FileContract", label: "Customer Aging (Pro)", minPlan: "free" },
      // { path: "/reports/professional/aged-creditors", icon: "FileContract", label: "Aged Creditors (Pro)", minPlan: "free" },
      // { path: "/reports/professional/vat", icon: "FileContract", label: "VAT (Pro)", minPlan: "free" },
      // { path: "/reports/professional/corporate-tax", icon: "FileContract", label: "Corporate Tax (Pro)", minPlan: "free" },
      // { path: "/reports/professional/payroll-by-department", icon: "FileContract", label: "Payroll by Dept (Pro)", minPlan: "free" },
      // { path: "/reports/professional/expense-by-category", icon: "FileContract", label: "Expense by Category (Pro)", minPlan: "free" },
    ],
  },
  {
    group: "Finance",
    icon: "Calculator",
    label: "Accounting",
    minPlan: "free",
    submenu: [
      { path: "/accounts", icon: "Book", label: "Chart of Accounts", minPlan: "free" },
      { path: "/accounts/journal", icon: "FileText", label: "Journal Vouchers", minPlan: "free" },
            { path: "/reports/ledger", icon: "Book", label: "General Ledger", requiredFeature: "basicLedger", minPlan: "free" },

      { path: "/accounts/financial", icon: "DollarSign", label: "Financial Accounts", minPlan: "free" },
      { path: "/accounts/fiscal-years", icon: "Calendar", label: "Fiscal Years", minPlan: "free" },
      { path: "/accounts/period-close", icon: "Lock", label: "Period Close", minPlan: "free" },
      { path: "/accounts/reconciliation", icon: "BalanceScale", label: "Bank Reconciliation", minPlan: "free" },
      { path: "/accounts/bank-transfer", icon: "TrendingUp", label: "Bank Transfer", minPlan: "free" },
      // { path: "/accounts/petty-cash", icon: "MoneyCheck", label: "Petty Cash", minPlan: "free" },
      { path: "/settings/cost-centers", icon: "Grid", label: "Cost Centers", minPlan: "free" },
      // { path: "/settings/exchange-rates", icon: "TrendingUp", label: "Exchange Rates", minPlan: "free" },
      // { path: "/settings/withholding-taxes", icon: "Tax", label: "Withholding Taxes", minPlan: "free" },
      { path: "/assets", icon: "Briefcase", label: "Fixed Assets", minPlan: "free" },
      // { path: "/assets/lifecycle", icon: "RefreshCw", label: "Asset Lifecycle", minPlan: "free" },
      // { path: "/assets/reconciliation", icon: "BalanceScale", label: "FA ↔ GL Recon", minPlan: "free" },
      // { path: "/assets/tagging", icon: "Tag", label: "Tagging", minPlan: "free" },
      // { path: "/accounts/document-lifecycle", icon: "FileContract", label: "Document Lifecycle", minPlan: "free" },
      // { path: "/accounts/credit-notes", icon: "RotateCcw", label: "Credit Notes", minPlan: "free" },
      { path: "/accounts/control-center", icon: "Shield", label: "Control Center", minPlan: "free" },
    ],
  },
  {
    group: "Finance",
    icon: "User",
    label: "HR",
    minPlan: "free",
    submenu: [
      { path: "/hr/operations", icon: "Clipboard", label: "Operations", minPlan: "free" },
      { path: "/hr/advances/new", icon: "HandHoldingUsd", label: "New Advance", minPlan: "free" },
      { path: "/hr/settlements/new", icon: "MoneyCheck", label: "New Settlement", minPlan: "free" },
      { path: "/hr/payslip", icon: "FileText", label: "Payslip", minPlan: "free" },
    ],
  },
  {
    group: "Operations",
    icon: "Warehouse",
    label: "Production",
    minPlan: "free",
    submenu: [
      { path: "/production/mrp", icon: "Layers", label: "MRP Runs", minPlan: "free" },
      { path: "/production/boms", icon: "Book", label: "Bill of Materials", minPlan: "free" },
      { path: "/production/work-orders", icon: "Clipboard", label: "Production Orders", minPlan: "free" },
      { path: "/production/quality", icon: "UserCheck", label: "Quality Checks", minPlan: "free" },
      { path: "/production/reports", icon: "BarChart", label: "Production Reports", minPlan: "free" },
    ],
  },
  {
    group: "Operations",
    icon: "DollarSign",
    label: "Sales",
    minPlan: "free",
    submenu: [
      { path: "/quotes", icon: "Quote", label: "Quotes", minPlan: "free", viewOnly: true },
      // Sales Orders hidden — no backend model in current build (60% scope
      // covers Quotes → Invoices → Payments directly; Sales Orders is
      // documented but not implemented).
      // { path: "/sales/orders", icon: "ShoppingBag", label: "Orders", minPlan: "pro" },
      { path: "/sales/invoices", icon: "FileText", label: "Invoices", minPlan: "pro" },
      { path: "/payments", icon: "CreditCard", label: "Payments", minPlan: "pro" },
      { path: "/accounts/credit-notes", icon: "RefreshCw", label: "Credit Notes", minPlan: "pro" },
    ],
  },
  {
    group: "Operations",
    icon: "ShoppingCart",
    label: "Purchases",
    minPlan: "free",
    submenu: [
      // Purchase Requests / Vendor Quotes / Returns hidden — no backend
      // models in current build (60% scope goes PO → GRN → Bill →
      // Payment; Requisition and Return workflows are documented but
      // not implemented).
      // { path: "/purchases/requisitions", icon: "RequestForm", label: "Purchase Requests", minPlan: "free" },
      // { path: "/purchases/quotes", icon: "VendorQuote", label: "Vendor Quotes", minPlan: "pro" },
      { path: "/purchases/orders", icon: "ShoppingCart", label: "Purchase Orders", minPlan: "pro" },
      { path: "/purchases/bills", icon: "Bill", label: "Bills", minPlan: "pro" },
      // { path: "/purchases/returns", icon: "RotateCcw", label: "Returns", minPlan: "pro" },
      { path: "/purchases/grn", icon: "Package", label: "GRNs", minPlan: "team" },
      { path: "/expense-bills", icon: "FileText", label: "Expense Bills", minPlan: "free" },
      // { path: "/purchases/reports", icon: "TrendingUp", label: "Reports", requiredFeature: "analytics", minPlan: "team" },
      // { path: "/purchases/analytics", icon: "PieChart", label: "Analytics", requiredFeature: "analytics", minPlan: "team" },
    ],
  },
  {
    group: "Operations",
    icon: "Package",
    label: "Inventory",
    minPlan: "free",
    submenu: [
      { path: "/categories", icon: "Tag", label: "Categories", minPlan: "free" },
      { path: "/inventory/list", icon: "List", label: "Item List", minPlan: "free" },
      { path: "/inventory/adjustments", icon: "RefreshCw", label: "Stock Adjustments", minPlan: "pro" },
      { path: "/inventory/transfers", icon: "Truck", label: "Transfers", minPlan: "pro" },
      { path: "/inventory/valuations", icon: "BarChart2", label: "Valuation", requiredFeature: "inventoryValuation", minPlan: "team" },
      { path: "/inventory/valuation-fifo", icon: "BarChart2", label: "FIFO Valuation", minPlan: "free" },
      // { path: "/inventory/reports", icon: "BarChart2", label: "Inventory Reports", requiredFeature: "analytics", minPlan: "team" },
      { path: "/services", icon: "Briefcase", label: "Services", minPlan: "free", viewOnly: true },
      { path: "/settings/units", icon: "Layers", label: "Units", minPlan: "free", viewOnly: true },
    ],
  },
  { group: "Operations", path: "/customers", icon: "Users", label: "Customers", minPlan: "free" },
  { group: "Operations", path: "/vendors", icon: "Truck", label: "Vendors", minPlan: "free" },
  // Administration section hidden for demo (out of 60% scope — this is
  // tenant admin plumbing, not client-facing ERP capability).
  // {
  //   group: "Administration",
  //   icon: "Shield",
  //   label: "Administration",
  //   requiredFeature: "collaboration",
  //   minPlan: "team",
  //   submenu: [
  //     { path: "/admin/users", icon: "UserCheck", label: "Users", minPlan: "team" },
  //     { path: "/admin/roles", icon: "Lock", label: "Roles", minPlan: "team" },
  //     { path: "/admin/user-roles", icon: "UserCheck", label: "User Roles", minPlan: "team" },
  //     { path: "/admin/user-permissions", icon: "Lock", label: "Permissions", minPlan: "team" },
  //     { path: "/admin/authority", icon: "Lock", label: "Approval Authority", minPlan: "team" },
  //     { path: "/admin/departments", icon: "Grid", label: "Departments", minPlan: "team" },
  //   ],
  // },
  {
    group: "Administration",
    icon: "Settings",
    label: "Settings",
    minPlan: "free",
    submenu: [
      { path: "/profile-settings", icon: "User", label: "Profile", minPlan: "free" },
      // { path: "/app-settings", icon: "Settings", label: "Preferences", minPlan: "free" },
      { path: "/settings/tax-codes", icon: "Tax", label: "Tax Codes", minPlan: "free" },
      { path: "/support", icon: "HelpCircle", label: "Support", minPlan: "free" },
      // { path: "/settings/2fa", icon: "Lock", label: "Two-Factor Auth", minPlan: "free" },
    ],
  },
  // Hidden for demo:
  // { group: "Support", path: "/guide", icon: "HelpCircle", label: "User Guide", minPlan: "free" },
];

// Wraps the matching substring of `text` in <mark> for search highlighting.
const highlight = (text, query) => {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="nx-mark">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
};

const Sidebar = () => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [showSidebarOnMobile, setShowSidebarOnMobile] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [flyoutPos, setFlyoutPos] = useState({ placement: "down" });
  const [query, setQuery] = useState("");
  // Per-org module toggles + page block list, fetched from
  // /api/my-modules. SuperAdmin turns these off per tenant in the
  // Site Owner console; this component hides the matching sidebar
  // entries. Null = still loading → show everything (fail-open).
  const [moduleAccess, setModuleAccess] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get("/my-modules");
        if (cancelled) return;
        const d = res.data?.data || res.data || {};
        setModuleAccess({
          modules: new Set(d.modules || []),
          disabledPages: (d.disabledPages || []).filter(Boolean),
        });
      } catch (_) {
        // Silent — if the endpoint isn't reachable, fall through to the
        // full menu so the user isn't stranded.
        if (!cancelled) setModuleAccess({ modules: null, disabledPages: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // True when the caller's org has this path enabled. Fail-open when
  // config is still loading or the path isn't in the module map.
  const isPathAllowed = (path) => {
    if (!moduleAccess) return true;
    if (moduleAccess.disabledPages.some((p) => path === p || path.startsWith(p + "/"))) return false;
    if (!moduleAccess.modules) return true;
    const mod = moduleForPath(path);
    if (!mod) return true;
    return moduleAccess.modules.has(mod);
  };
  const sidebarRef = useRef(null);
  const searchInputRef = useRef(null);

  const { companyInfo } = useCompany();
  const { canAccessFeature, getSubscriptionPlan } = useSubscription();
  const { user } = useAuth();
  const { darkMode } = useContext(ThemeContext);

  useEffect(() => {
    const savedState = localStorage.getItem("sidebarCollapsed");
    if (savedState) setIsCollapsed(JSON.parse(savedState));

    const savedExpandedItems = localStorage.getItem("expandedItems");
    if (savedExpandedItems) setExpandedItems(JSON.parse(savedExpandedItems));

    const handleResize = () => {
      if (window.innerWidth <= 768) setIsCollapsed(true);
    };
    handleResize();
    window.addEventListener("resize", handleResize);

    const handleStorageChange = () => {
      const currentState = localStorage.getItem("sidebarCollapsed");
      if (currentState) setIsCollapsed(JSON.parse(currentState));
    };
    window.addEventListener("storage", handleStorageChange);

    // Single sync contract shared with Header.jsx: whichever component
    // changes the collapsed state broadcasts it here, and both sides
    // listen so the sidebar-edge handle and the header's toggle button
    // always agree, in the same tab, instantly.
    const handleCollapseBroadcast = (e) => setIsCollapsed(e.detail.isCollapsed);
    window.addEventListener("sidebar:collapse-change", handleCollapseBroadcast);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("sidebar:collapse-change", handleCollapseBroadcast);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(isCollapsed));
    window.dispatchEvent(new CustomEvent("sidebar:collapse-change", { detail: { isCollapsed } }));
  }, [isCollapsed]);

  const getActiveParentMenu = () =>
    menuItems.find(
      (item) =>
        item.submenu &&
        item.submenu.some((subItem) => location.pathname.startsWith(subItem.path))
    );

  const isMenuItemActive = (item) => {
    if (item.path) return location.pathname === item.path;
    if (item.submenu) return item.submenu.some((s) => location.pathname === s.path);
    return false;
  };

  useEffect(() => {
    const activeParent = getActiveParentMenu();
    if (activeParent) {
      setExpandedItems((prev) => ({ ...prev, [activeParent.label]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCollapsed && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        const activeParent = getActiveParentMenu();
        setExpandedItems(activeParent ? { [activeParent.label]: true } : {});
        setActiveSubmenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapsed]);

  const toggleSidebarOnMobile = () => setShowSidebarOnMobile((v) => !v);

  const expandFromCollapsedSearch = () => {
    setIsCollapsed(false);
    requestAnimationFrame(() => searchInputRef.current?.focus());
  };

  const toggleSubmenu = (label, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isCollapsed) {
      const rect = e.currentTarget.getBoundingClientRect();
      const estimatedHeight = 44 * 6;
      const placement = window.innerHeight - rect.top < estimatedHeight ? "up" : "down";
      setFlyoutPos({
        top: placement === "down" ? rect.top : undefined,
        bottom: placement === "up" ? window.innerHeight - rect.bottom : undefined,
        placement,
      });
      setActiveSubmenu((prev) => (prev === label ? null : label));
    } else {
      setExpandedItems((prev) => ({ ...prev, [label]: !prev[label] }));
    }
  };

  const handleMouseEnter = (label, e) => {
    if (!isCollapsed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const estimatedHeight = 44 * 6;
    const placement = window.innerHeight - rect.top < estimatedHeight ? "up" : "down";
    setFlyoutPos({
      top: placement === "down" ? rect.top : undefined,
      bottom: placement === "up" ? window.innerHeight - rect.bottom : undefined,
      placement,
    });
    setActiveSubmenu(label);
  };

  const handleMouseLeave = () => {
    if (isCollapsed) setActiveSubmenu(null);
  };

  // Access control lives entirely on the SA-managed per-org `pageRoles`
  // map (see PageRolesTab in pages/superadmin/SuperAdmin.js), so every
  // authenticated user sees every item here — only search narrows the list.
  const filteredMenuItems = useMemo(() => {
    // Two-pass filter:
    //   1. Drop entries the org's module config has turned off.
    //   2. Then apply the search query. Doing module-filtering first
    //      keeps the search "no results" state accurate for disabled
    //      modules — we don't want the user typing "payroll" and seeing
    //      matches for a module that's turned off.
    const byAccess = menuItems
      .map((item) => {
        if (item.submenu) {
          const kids = item.submenu.filter((s) => isPathAllowed(s.path));
          if (item.path && !isPathAllowed(item.path)) return null;
          // If a parent has no accessible children, hide the whole group.
          if (item.submenu.length && kids.length === 0) return null;
          return { ...item, submenu: kids };
        }
        return item.path && !isPathAllowed(item.path) ? null : item;
      })
      .filter(Boolean);

    const q = query.trim().toLowerCase();
    if (!q) return byAccess;

    return byAccess
      .map((item) => {
        const selfMatch = item.label.toLowerCase().includes(q);
        if (item.submenu) {
          const matchedSub = item.submenu.filter((s) => s.label.toLowerCase().includes(q));
          if (selfMatch) return item; // show whole group as-is
          if (matchedSub.length) return { ...item, submenu: matchedSub, forceOpen: true };
          return null;
        }
        return selfMatch ? item : null;
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, moduleAccess]);

  const isSearching = query.trim().length > 0;

  const themeClass = darkMode ? "" : "nx-light";

  const displayName = user?.name || user?.fullName || user?.email || "Account";
  const displayRole = companyInfo?.name || getSubscriptionPlan?.() || "Workspace";
  const initials = displayName
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  let lastGroup = null;

  return (
    <>
      <button
        type="button"
        className="nx-mobile-toggle border-0"
        onClick={toggleSidebarOnMobile}
        aria-label="Toggle navigation"
      >
        <FontAwesomeIcon icon={showSidebarOnMobile ? faTimes : faBars} />
      </button>

      <nav
        ref={sidebarRef}
        className={`nx-sidebar d-flex flex-column ${themeClass} ${
          isCollapsed ? "collapsed" : ""
        } ${showSidebarOnMobile ? "mobile-active" : ""}`}
      >
        {/* Collapse is triggered from Header.jsx (single control, synced via
            the `sidebar:collapse-change` event) so there's only one toggle
            on screen instead of two competing buttons. */}

        <div className="nx-sidebar__brand d-flex align-items-center">
          <img
            src={isCollapsed ? miniLogo : darkMode ? lightLogo : darkLogo}
            alt="NUMRA"
            className="nx-sidebar__brand-img"
          />
        </div>

        <div className="nx-search">
          {!isCollapsed ? (
            <div className="nx-search__box">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="nx-search__icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="nx-search__input"
                placeholder="Search menu"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {isSearching && (
                <button
                  type="button"
                  className="nx-search__clear border-0"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="nx-search__collapsed-btn border-0"
              onClick={expandFromCollapsedSearch}
              aria-label="Search menu"
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </button>
          )}
        </div>

        <div className="nx-sidebar__body">
          {filteredMenuItems.length === 0 ? (
            <div className="nx-empty">No menu items match "{query}"</div>
          ) : (
            <ul className="nx-nav list-unstyled d-flex flex-column m-0 p-0">
              {filteredMenuItems.map((item, index) => {
                const isOpen = isSearching ? true : !!expandedItems[item.label];
                const isFlyoutOpen = isCollapsed && activeSubmenu === item.label;
                const isActive = isMenuItemActive(item);
                const showGroupLabel = !isSearching && item.group && item.group !== lastGroup;
                lastGroup = item.group || lastGroup;

                const rowStyle = { "--i": index };

                return (
                  <React.Fragment key={item.label}>
                    {showGroupLabel && <li className="nx-group-label">{item.group}</li>}

                    {item.submenu ? (
                      <li
                        className={`nx-item ${isActive ? "active-parent" : ""}`}
                        style={rowStyle}
                        onMouseEnter={(e) => handleMouseEnter(item.label, e)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <div
                          className={`nx-link ${isActive ? "active" : ""}`}
                          onClick={(e) => toggleSubmenu(item.label, e)}
                          role="button"
                          tabIndex={0}
                        >
                          <span className="nx-icon">
                            <FontAwesomeIcon icon={iconMap[item.icon]} />
                          </span>

                          {!isCollapsed && (
                            <>
                              <span className="nx-label">
                                {highlight(item.label, query)}
                                {item.viewOnly && <span className="nx-badge">View only</span>}
                              </span>
                              <span className={`nx-caret ${isOpen ? "is-open" : ""}`}>
                                <FontAwesomeIcon icon={faChevronDown} />
                              </span>
                            </>
                          )}

                          {isCollapsed && <span className="nx-tooltip">{item.label}</span>}
                        </div>

                        {!isCollapsed && (
                          <div className={`nx-submenu-wrap ${isOpen ? "is-open" : ""}`}>
                            <ul className="nx-submenu">
                              <div className="nx-submenu__inner">
                                {item.submenu.map((sub, i) => {
                                  const subActive = location.pathname.startsWith(sub.path);
                                  return (
                                    <li
                                      key={sub.path}
                                      className="nx-sub-item"
                                      style={{ "--d": `${i * 0.035}s` }}
                                    >
                                      <Link to={sub.path} className={`nx-sublink ${subActive ? "active" : ""}`}>
                                        <span className="nx-sub-icon">
                                          <FontAwesomeIcon icon={iconMap[sub.icon]} />
                                        </span>
                                        <span className="text-truncate">
                                          {highlight(sub.label, query)}
                                          {sub.viewOnly && <span className="nx-badge">View only</span>}
                                        </span>
                                      </Link>
                                    </li>
                                  );
                                })}
                              </div>
                            </ul>
                          </div>
                        )}

                        {isCollapsed && (
                          <div
                            className={`nx-flyout ${isFlyoutOpen ? "show" : ""}`}
                            style={
                              flyoutPos.placement === "down"
                                ? { top: flyoutPos.top }
                                : { bottom: flyoutPos.bottom }
                            }
                          >
                            <div className="nx-flyout__title">{item.label}</div>
                            <ul className="list-unstyled d-flex flex-column gap-1 m-0 p-0">
                              {item.submenu.map((sub) => {
                                const subActive = location.pathname.startsWith(sub.path);
                                return (
                                  <li key={sub.path}>
                                    <Link
                                      to={sub.path}
                                      className={`nx-sublink ${subActive ? "active" : ""}`}
                                      onClick={() => setActiveSubmenu(null)}
                                    >
                                      <span className="nx-sub-icon">
                                        <FontAwesomeIcon icon={iconMap[sub.icon]} />
                                      </span>
                                      <span className="text-truncate">
                                        {sub.label}
                                        {sub.viewOnly && <span className="nx-badge">View only</span>}
                                      </span>
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </li>
                    ) : (
                      <li className="nx-item" style={rowStyle}>
                        <Link
                          to={item.path}
                          className={`nx-link ${location.pathname === item.path ? "active" : ""}`}
                          onClick={() => isCollapsed && setActiveSubmenu(null)}
                        >
                          <span className="nx-icon">
                            <FontAwesomeIcon icon={iconMap[item.icon]} />
                          </span>
                          {!isCollapsed && (
                            <span className="nx-label">
                              {highlight(item.label, query)}
                              {item.viewOnly && <span className="nx-badge">View only</span>}
                            </span>
                          )}
                          {isCollapsed && <span className="nx-tooltip">{item.label}</span>}
                        </Link>
                      </li>
                    )}
                  </React.Fragment>
                );
              })}
            </ul>
          )}
        </div>

        <div className="nx-footer">
          <Link to="/profile-settings" className="nx-footer__card text-decoration-none">
            <div className="nx-footer__avatar">{initials || "U"}</div>
            {!isCollapsed && (
              <>
                <div className="nx-footer__meta">
                  <div className="nx-footer__name">{displayName}</div>
                  <div className="nx-footer__role">{displayRole}</div>
                </div>
                <FontAwesomeIcon icon={faChevronDown} className="nx-footer__chevron" />
              </>
            )}
          </Link>
        </div>
      </nav>

      <div
        className={`nx-overlay ${showSidebarOnMobile ? "active" : ""}`}
        onClick={toggleSidebarOnMobile}
      />
    </>
  );
};

export default Sidebar;